import pluralize from 'pluralize';
import moment, { Moment } from 'moment';
import { isEmpty, omitBy } from 'lodash';
import AdConfigModel from '@mm/state/model/AdConfigModel';
import {
  AdSetBudgetType,
  CampaignBudgetType,
} from '@mm/state/interfaces/adAccounts';
import { BUDGET_THRESHOLD } from '../../constants/constants';
import { FormValues } from './types';

const preciseAmount = (amount: number, offset: number) => {
  if (isNaN(amount)) {
    return NaN;
  }
  return Math.round(amount * offset);
};

const budgetThreshold = (offset: number) => {
  return BUDGET_THRESHOLD * offset;
};

const bidCapValidation = ({ bidAmount }: FormValues) => {
  if (bidAmount === undefined || bidAmount === null || bidAmount === '') {
    return [];
  }
  const numericAmount = preciseAmount(Number(bidAmount), 1);
  if (isNaN(numericAmount)) {
    return ['Bid amount is not a number'];
  }
  if (numericAmount > budgetThreshold(1)) {
    return ['Amount is too large'];
  }
  if (numericAmount < 1) {
    return ['Bid amount can not be lower than $1'];
  }
  return [];
};

const fullDays = (
  adConfig: AdConfigModel | null,
  startDate: Moment,
  endDate: Moment,
) => {
  const days =
    adConfig && adConfig.adSetEndDate && startDate
      ? 1 +
        Math.ceil(endDate.diff(moment.max(moment(), startDate), 'days', true))
      : 1;
  return Math.max(days, 1);
};

const adSetBudgetValidation = (
  adConfig: AdConfigModel | null,
  {
    adSetBudgetType,
    adSetBudget,
    adSetStartDate,
    adSetEndDate,
    adAccount,
    bidAmount,
  }: FormValues,
  budgetOn: boolean,
) => {
  if (!budgetOn) {
    return [];
  }
  const numericAdSetBudget =
    adSetBudget === undefined ? 0 : preciseAmount(Number(adSetBudget), 1);
  if (isNaN(numericAdSetBudget)) {
    return ['Ad Set budget is not a number'];
  }
  if (numericAdSetBudget > budgetThreshold(1)) {
    return ['Budget value is too large'];
  }
  const numericBidAmount =
    bidAmount === undefined ? 0 : preciseAmount(Number(bidAmount), 1);
  const days = fullDays(adConfig, adSetStartDate, adSetEndDate);
  const adSetDailyBudget =
    adSetBudgetType === AdSetBudgetType.DAILY
      ? numericAdSetBudget
      : numericAdSetBudget / days;
  if (!isNaN(numericBidAmount) && adSetDailyBudget < numericBidAmount) {
    return [
      adSetBudgetType === AdSetBudgetType.DAILY
        ? 'Daily ad set budget can not be lower than bid amount'
        : 'Lifetime ad set budget can not be lower than bid amount * number of days scheduled',
    ];
  }
  if (adSetDailyBudget < 1) {
    return [
      adSetBudgetType === AdSetBudgetType.DAILY
        ? 'Daily ad set budget can not be lower than $1'
        : 'Lifetime ad set budget should be at least 1$ per each scheduled day"',
    ];
  }
  return [];
};

const campaignBudgetValidation = (
  adConfig: AdConfigModel | null,
  {
    campaignBudget,
    campaignBudgetType,
    adSetBudget,
    adSetBudgetType,
    adSetStartDate,
    adSetEndDate,
    bidAmount,
  }: FormValues,
  budgetOn: boolean,
) => {
  if (!budgetOn) {
    return [];
  }
  const numericCampaignBudget =
    campaignBudget === undefined ? 0 : preciseAmount(Number(campaignBudget), 1);
  if (isNaN(numericCampaignBudget)) {
    return ['Campaign budget is not a number'];
  }
  if (numericCampaignBudget > budgetThreshold(1)) {
    return ['Budget value is too large'];
  }
  const numericBidAmount =
    bidAmount === undefined ? 0 : preciseAmount(Number(bidAmount), 1);
  const days = fullDays(adConfig, adSetStartDate, adSetEndDate);
  const daysText = `ad is currently scheduled to run for ${days} ${pluralize(
    'day',
    days,
  )}`;
  const campaignDailyBudget =
    campaignBudgetType === CampaignBudgetType.DAILY
      ? numericCampaignBudget
      : numericCampaignBudget / days;
  if (!isNaN(numericBidAmount) && adConfig?.bidAmount !== null) {
    if (campaignDailyBudget < numericBidAmount) {
      return [
        campaignBudgetType === CampaignBudgetType.DAILY
          ? 'Daily campaign budget can not be lower than bid amount'
          : `Lifetime campaign budget can not be lower than bid amount * number of days scheduled (${daysText})`,
      ];
    }
  }
  if (campaignDailyBudget < 1) {
    return [
      campaignBudgetType === CampaignBudgetType.DAILY
        ? 'Daily campaign budget can not be lower than $1'
        : `Lifetime campaign budget should be at least 1$ per each scheduled day (${daysText})`,
    ];
  }
  return [];
};

const creativeTextValidation = ({ creativeText }: FormValues) => {
  if (creativeText) {
    return [];
  }
  return ['Creative text can not be empty'];
};

const creativeHeadlineValidation = ({ creativeHeadline }: FormValues) => {
  if (creativeHeadline) {
    return [];
  }
  return ['Creative headline can not be empty'];
};

const thumbValidation = (
  formValues: FormValues,
  adConfig: AdConfigModel | null,
  isVideo: boolean,
) => {
  if (
    !isVideo ||
    formValues.thumbUrl ||
    (!formValues.sourceAd && adConfig?.sourceAdId)
  ) {
    return [];
  }
  return ['A thumbnail is required for a video creative'];
};

const assetValidation = (
  formValues: FormValues,
  adConfig: AdConfigModel | null,
  isVideo: boolean,
) => {
  if (
    (isVideo && formValues.videoUrl) ||
    (!isVideo && formValues.imageUrl) ||
    (!formValues.sourceAd && adConfig?.sourceAdId)
  ) {
    return [];
  }
  return ['A video or an image is required'];
};

export const prepareValidations = (
  adConfig: AdConfigModel | null,
  formValues: FormValues,
  remoteValidations: { [key in keyof FormValues]?: string[] },
  mismatchingAudiences: string[],
  campaignBudgetOn: boolean,
  isVideo: boolean,
) =>
  omitBy(
    {
      ...remoteValidations,
      creativeText: [
        ...creativeTextValidation(formValues),
        ...(remoteValidations['creativeText'] || []),
      ],
      creativeHeadline: [
        ...creativeHeadlineValidation(formValues),
        ...(remoteValidations['creativeHeadline'] || []),
      ],
      campaignBudget: [
        ...campaignBudgetValidation(adConfig, formValues, campaignBudgetOn),
        ...(remoteValidations['campaignBudget'] || []),
      ],
      adSetBudget: [
        ...adSetBudgetValidation(adConfig, formValues, !campaignBudgetOn),
        ...(remoteValidations['adSetBudget'] || []),
      ],
      bidAmount: [
        ...bidCapValidation(formValues),
        ...(remoteValidations['bidAmount'] || []),
      ],
      adAccount: [
        ...(formValues.adAccount === null && !adConfig?.adAccountId
          ? ['Ad Account can not be empty']
          : []),
      ],
      sourceAdSet: [
        ...(formValues.sourceAdSet === null &&
        adConfig?.targeting === null &&
        !adConfig?.sourceAdSetId
          ? ['Source Ad Set can not be empty']
          : []),
        ...(mismatchingAudiences.length > 0
          ? ['Ad account does not have access to some custom audiences']
          : []),
        ...(remoteValidations['sourceAdSet'] || []),
      ],
      assetId: [...assetValidation(formValues, adConfig, isVideo)],
      thumbId: [...thumbValidation(formValues, adConfig, isVideo)],
    },
    isEmpty,
  );
